import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, RefreshCw, Search, Users, CheckCircle, XCircle, 
  Key, Shield, Activity, FileText, BarChart3, 
  Calendar, UserCheck, UserX, AlertTriangle, Lock, LogOut,
  ClipboardList, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Eye, User, Building, UserPlus, Briefcase, Edit, Save, Trash2, Database, LifeBuoy
} from 'lucide-react';
import { toast } from 'sonner';
import LegacyUsersTab from '../components/LegacyUsersTab';
import ManualPostulationModal from '../components/ManualPostulationModal';
import SupportTicketsTab from '../components/SupportTicketsTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminDashboard() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);
  
  // Postulaciones state
  const [postulaciones, setPostulaciones] = useState([]);
  const [totalPostulaciones, setTotalPostulaciones] = useState(0);
  const [postulacionesPage, setPostulacionesPage] = useState(1);
  const [showManualPostModal, setShowManualPostModal] = useState(false);
  const [postulacionesTotalPages, setPostulacionesTotalPages] = useState(1);
  const [filterFuncionario, setFilterFuncionario] = useState('');
  const [filterConvocatoria, setFilterConvocatoria] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [funcionariosList, setFuncionariosList] = useState([]);
  const [convocatoriasList, setConvocatoriasList] = useState([]);
  const [viewPostulacionModal, setViewPostulacionModal] = useState({ open: false, postulacion: null });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVerified, setFilterVerified] = useState('');
  const [filterUserType, setFilterUserType] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [startDate, setStartDate] = useState('2026-03-01');
  const [endDate, setEndDate] = useState('');
  
  // Modals
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  
  // Create Funcionario Modal
  const [showCreateFuncionarioModal, setShowCreateFuncionarioModal] = useState(false);
  const [creatingFuncionario, setCreatingFuncionario] = useState(false);
  const [newFuncionario, setNewFuncionario] = useState({
    email: '',
    password: '',
    first_name: '',
    second_name: '',
    first_lastname: '',
    second_lastname: '',
    document_type: 'CC',
    document_id: ''
  });

  // Edit User Modal
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({});
  const [savingUser, setSavingUser] = useState(false);
  const [showPinConfirm, setShowPinConfirm] = useState(false);
  const [pinInput, setPinInput] = useState('');

  // Delete User Modal
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deletePin, setDeletePin] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    if (user?.user_type !== 'superadmin') {
      toast.error('Acceso no autorizado');
      navigate('/dashboard');
      return;
    }
    fetchDashboard();
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'logs') fetchLogs();
    if (activeTab === 'postulaciones') fetchPostulaciones();
  }, [activeTab, filterVerified, filterUserType, filterAction, startDate, endDate]);

  useEffect(() => {
    if (activeTab === 'postulaciones') fetchPostulaciones();
  }, [postulacionesPage, filterFuncionario, filterConvocatoria, filterEstado]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/superadmin/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar dashboard');
      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      toast.error('Error al cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/superadmin/users?limit=200`;
      if (filterVerified !== '') url += `&verified=${filterVerified}`;
      if (filterUserType) url += `&user_type=${filterUserType}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      if (startDate) url += `&start_date=${startDate}T00:00:00`;
      if (endDate) url += `&end_date=${endDate}T23:59:59`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar usuarios');
      const data = await response.json();
      setUsers(data.users || []);
      setTotalUsers(data.total || 0);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/superadmin/logs?limit=500`;
      if (filterAction) url += `&action=${filterAction}`;
      if (searchTerm) url += `&email=${encodeURIComponent(searchTerm)}`;
      if (startDate) url += `&start_date=${startDate}T00:00:00`;
      if (endDate) url += `&end_date=${endDate}T23:59:59`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar logs');
      const data = await response.json();
      setLogs(data.logs || []);
      setTotalLogs(data.total || 0);
    } catch (error) {
      toast.error('Error al cargar logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostulaciones = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/superadmin/postulaciones?page=${postulacionesPage}&limit=25`;
      if (filterFuncionario) url += `&funcionario_id=${filterFuncionario}`;
      if (filterConvocatoria) url += `&convocatoria_id=${filterConvocatoria}`;
      if (filterEstado) url += `&estado=${filterEstado}`;
      if (searchTerm) url += `&email=${encodeURIComponent(searchTerm)}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar postulaciones');
      const data = await response.json();
      setPostulaciones(data.postulaciones || []);
      setTotalPostulaciones(data.total || 0);
      setPostulacionesTotalPages(data.total_pages || 1);
      setFuncionariosList(data.filters?.funcionarios || []);
      setConvocatoriasList(data.filters?.convocatorias || []);
    } catch (error) {
      toast.error('Error al cargar postulaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId, verify) => {
    try {
      const endpoint = verify ? 'verify' : 'unverify';
      const response = await fetch(`${API_URL}/api/superadmin/users/${userId}/${endpoint}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error');
      toast.success(verify ? 'Usuario verificado' : 'Verificacion removida');
      fetchUsers();
      fetchDashboard();
    } catch (error) {
      toast.error('Error al actualizar usuario');
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/superadmin/users/${selectedUser.id}/password`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_password: newPassword })
      });
      if (!response.ok) throw new Error('Error');
      toast.success('Contrasena temporal establecida exitosamente');
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error('Error al cambiar contrasena');
    }
  };

  const handleCreateFuncionario = async () => {
    // Validate required fields
    if (!newFuncionario.email || !newFuncionario.password || !newFuncionario.first_name || 
        !newFuncionario.first_lastname || !newFuncionario.document_id) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }
    if (newFuncionario.password.length < 6) {
      toast.error('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      setCreatingFuncionario(true);
      const response = await fetch(`${API_URL}/api/superadmin/funcionarios`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newFuncionario)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al crear funcionario');
      }
      
      toast.success(`Funcionario ${data.funcionario.nombre} creado exitosamente`);
      setShowCreateFuncionarioModal(false);
      setNewFuncionario({
        email: '',
        password: '',
        first_name: '',
        second_name: '',
        first_lastname: '',
        second_lastname: '',
        document_type: 'CC',
        document_id: ''
      });
      fetchUsers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreatingFuncionario(false);
    }
  };

  // Función para abrir modal de edición
  const handleOpenEditUser = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/superadmin/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Error al cargar usuario');
      const userData = await response.json();
      setEditingUser(userData);
      setEditUserData(userData);
      setShowEditUserModal(true);
    } catch (error) {
      toast.error('Error al cargar datos del usuario');
    }
  };

  // Función para actualizar campo en el formulario
  const handleEditFieldChange = (field, value) => {
    setEditUserData(prev => ({ ...prev, [field]: value }));
  };

  // Función para mostrar confirmación de PIN
  const handleRequestSaveUser = () => {
    setShowPinConfirm(true);
    setPinInput('');
  };

  // Función para guardar usuario con PIN
  const handleSaveUserWithPin = async () => {
    if (!pinInput || pinInput.length !== 6) {
      toast.error('Ingrese el PIN de 6 dígitos');
      return;
    }

    try {
      setSavingUser(true);
      const response = await fetch(`${API_URL}/api/superadmin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...editUserData, pin: pinInput })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al actualizar usuario');
      }
      
      toast.success('Usuario actualizado correctamente');
      setShowEditUserModal(false);
      setShowPinConfirm(false);
      setEditingUser(null);
      setEditUserData({});
      setPinInput('');
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingUser(false);
    }
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditUserModal(false);
    setShowPinConfirm(false);
    setEditingUser(null);
    setEditUserData({});
    setPinInput('');
  };

  // Abrir modal de eliminación
  const handleOpenDeleteUser = (user) => {
    setDeletingUser(user);
    setDeletePin('');
    setShowDeleteUserModal(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteUserModal(false);
    setDeletingUser(null);
    setDeletePin('');
  };

  // Eliminar usuario con PIN
  const handleDeleteUserWithPin = async () => {
    if (!deletePin || deletePin.length !== 6) {
      toast.error('Ingrese el PIN de 6 dígitos');
      return;
    }

    try {
      setIsDeleting(true);
      const response = await fetch(`${API_URL}/api/superadmin/users/${deletingUser.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pin: deletePin })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al eliminar usuario');
      }
      
      toast.success(`Usuario ${data.deleted_email} eliminado correctamente`);
      handleCloseDeleteModal();
      fetchUsers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('es-CO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getActionColor = (action, success) => {
    if (!success) return 'bg-red-100 text-red-800';
    const colors = {
      'LOGIN': 'bg-blue-100 text-blue-800',
      'REGISTER': 'bg-green-100 text-green-800',
      'VERIFY': 'bg-purple-100 text-purple-800',
      'POSTULACION': 'bg-amber-100 text-amber-800',
      'ADMIN_VERIFY': 'bg-indigo-100 text-indigo-800',
      'ADMIN_PASSWORD_CHANGE': 'bg-orange-100 text-orange-800',
      'LOGIN_FAILED': 'bg-red-100 text-red-800',
      'VERIFY_FAILED': 'bg-red-100 text-red-800'
    };
    return colors[action] || 'bg-slate-100 text-slate-800';
  };

  const getUserTypeBadge = (type) => {
    const styles = {
      'funcionario': 'bg-purple-100 text-purple-800',
      'juridica': 'bg-amber-100 text-amber-800',
      'natural': 'bg-blue-100 text-blue-800',
      'superadmin': 'bg-red-100 text-red-800'
    };
    const labels = {
      'funcionario': 'Funcionario',
      'juridica': 'Juridica',
      'natural': 'Natural',
      'superadmin': 'Super Admin'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${styles[type] || 'bg-slate-100'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
          <Users className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{dashboardData?.users?.total || 0}</p>
          <p className="text-sm opacity-80">Total Usuarios</p>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-5 text-white shadow-lg">
          <UserCheck className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{dashboardData?.users?.verified || 0}</p>
          <p className="text-sm opacity-80">Verificados</p>
        </div>
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-5 text-white shadow-lg">
          <UserX className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{dashboardData?.users?.unverified || 0}</p>
          <p className="text-sm opacity-80">Sin Verificar</p>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-5 text-white shadow-lg">
          <Activity className="h-8 w-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{dashboardData?.logs?.total || 0}</p>
          <p className="text-sm opacity-80">Logs desde Mar 1</p>
        </div>
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-amber-500">
          <p className="text-sm text-slate-500">Persona Natural</p>
          <p className="text-2xl font-bold text-slate-900">{dashboardData?.users?.natural || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-indigo-500">
          <p className="text-sm text-slate-500">Persona Juridica</p>
          <p className="text-2xl font-bold text-slate-900">{dashboardData?.users?.juridica || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-slate-500">
          <p className="text-sm text-slate-500">Funcionarios</p>
          <p className="text-2xl font-bold text-slate-900">{dashboardData?.users?.funcionario || 0}</p>
        </div>
      </div>

      {/* Alert Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h3 className="font-semibold">Logins Fallidos</h3>
          </div>
          <p className="text-3xl font-bold text-red-600">{dashboardData?.logs?.failed_logins || 0}</p>
          <p className="text-sm text-slate-500">Desde el 1 de Marzo 2026</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-green-500" />
            <h3 className="font-semibold">Registros Nuevos</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">{dashboardData?.logs?.registrations || 0}</p>
          <p className="text-sm text-slate-500">Desde el 1 de Marzo 2026</p>
        </div>
      </div>

      {/* Recent Users */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          Ultimos Usuarios Registrados
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left">Email</th>
                <th className="px-3 py-2 text-left">Nombre</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-center">Verificado</th>
                <th className="px-3 py-2 text-left">Fecha</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {dashboardData?.recent_users?.map((u) => (
                <tr key={u.id || u.email} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium">{u.email}</td>
                  <td className="px-3 py-2">{u.first_name || u.company_name || '-'} {u.first_lastname || ''}</td>
                  <td className="px-3 py-2">{getUserTypeBadge(u.user_type)}</td>
                  <td className="px-3 py-2 text-center">
                    {u.verified ? <CheckCircle className="h-5 w-5 text-green-500 mx-auto" /> : <XCircle className="h-5 w-5 text-red-500 mx-auto" />}
                  </td>
                  <td className="px-3 py-2 text-slate-500">{formatDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Security Info */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Lock className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-green-800">Seguridad de Contrasenas</h4>
            <p className="text-sm text-green-700 mt-1">
              Las contrasenas se almacenan de forma segura usando hash bcrypt. 
              El Super Admin puede establecer contrasenas temporales para ayudar con la recuperacion de cuentas.
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por email, nombre, documento, NIT..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="search-users-input"
              />
            </div>
          </div>
          <select 
            value={filterVerified} 
            onChange={(e) => setFilterVerified(e.target.value)} 
            className="px-4 py-2 border rounded-lg"
            data-testid="filter-verified"
          >
            <option value="">Todos los estados</option>
            <option value="true">Verificados</option>
            <option value="false">Sin verificar</option>
          </select>
          <select 
            value={filterUserType} 
            onChange={(e) => setFilterUserType(e.target.value)} 
            className="px-4 py-2 border rounded-lg"
            data-testid="filter-user-type"
          >
            <option value="">Todos los tipos</option>
            <option value="natural">Persona Natural</option>
            <option value="juridica">Persona Juridica</option>
            <option value="funcionario">Funcionario</option>
          </select>
          <input 
            type="date" 
            value={startDate} 
            onChange={(e) => setStartDate(e.target.value)} 
            className="px-4 py-2 border rounded-lg" 
          />
          <input 
            type="date" 
            value={endDate} 
            onChange={(e) => setEndDate(e.target.value)} 
            className="px-4 py-2 border rounded-lg" 
            placeholder="Hasta" 
          />
          <button 
            onClick={fetchUsers} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            data-testid="search-users-btn"
          >
            <Search className="h-4 w-4" /> Buscar
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800 flex items-center justify-between">
        <span>Mostrando {users.length} de {totalUsers} usuarios</span>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Contrasenas almacenadas de forma segura
          </span>
          <button
            onClick={() => setShowCreateFuncionarioModal(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition-colors"
            data-testid="btn-create-funcionario"
          >
            <UserPlus className="h-4 w-4" /> Crear Funcionario
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Nombre/Empresa</th>
                <th className="px-4 py-3 text-left">Documento/NIT</th>
                <th className="px-4 py-3 text-left">Tipo</th>
                <th className="px-4 py-3 text-center">Verificado</th>
                <th className="px-4 py-3 text-left">Fecha Registro</th>
                <th className="px-4 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-blue-600">{u.email}</td>
                  <td className="px-4 py-3">
                    {u.first_name || u.company_name || '-'} {u.first_lastname || ''}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm">{u.document_id || u.nit || '-'}</td>
                  <td className="px-4 py-3">{getUserTypeBadge(u.user_type)}</td>
                  <td className="px-4 py-3 text-center">
                    {u.verified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3" /> Si
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                        <XCircle className="h-3 w-3" /> No
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleOpenEditUser(u.id)}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                        title="Editar perfil completo"
                        data-testid={`edit-${u.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {u.verified ? (
                        <button
                          onClick={() => handleVerifyUser(u.id, false)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remover verificacion"
                          data-testid={`unverify-${u.id}`}
                        >
                          <UserX className="h-4 w-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleVerifyUser(u.id, true)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                          title="Verificar usuario"
                          data-testid={`verify-${u.id}`}
                        >
                          <UserCheck className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => { setSelectedUser(u); setShowPasswordModal(true); }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Establecer contrasena temporal"
                        data-testid={`change-password-${u.id}`}
                      >
                        <Key className="h-4 w-4" />
                      </button>
                      {u.user_type !== 'superadmin' && (
                        <button
                          onClick={() => handleOpenDeleteUser(u)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Eliminar usuario"
                          data-testid={`delete-${u.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-slate-500">
                    No se encontraron usuarios con los filtros seleccionados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderLogs = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por email..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="search-logs-input"
              />
            </div>
          </div>
          <select 
            value={filterAction} 
            onChange={(e) => setFilterAction(e.target.value)} 
            className="px-4 py-2 border rounded-lg"
            data-testid="filter-action"
          >
            <option value="">Todas las acciones</option>
            <option value="LOGIN">Login</option>
            <option value="LOGIN_FAILED">Login Fallido</option>
            <option value="REGISTER">Registro</option>
            <option value="VERIFY">Verificacion</option>
            <option value="VERIFY_FAILED">Verificacion Fallida</option>
            <option value="POSTULACION">Postulacion</option>
            <option value="ADMIN_VERIFY">Admin Verificacion</option>
            <option value="ADMIN_PASSWORD_CHANGE">Admin Cambio Contrasena</option>
          </select>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <input 
              type="date" 
              value={startDate} 
              onChange={(e) => setStartDate(e.target.value)} 
              className="px-4 py-2 border rounded-lg" 
            />
            <span className="text-slate-400">-</span>
            <input 
              type="date" 
              value={endDate} 
              onChange={(e) => setEndDate(e.target.value)} 
              className="px-4 py-2 border rounded-lg" 
            />
          </div>
          <button 
            onClick={fetchLogs} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            data-testid="search-logs-btn"
          >
            <Search className="h-4 w-4" /> Buscar
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-purple-50 rounded-lg p-3 text-sm text-purple-800">
        Mostrando {logs.length} de {totalLogs} logs (desde {startDate || '2026-03-01'})
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="logs-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">Fecha/Hora</th>
                <th className="px-4 py-3 text-left">Accion</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Detalles</th>
                <th className="px-4 py-3 text-center">Estado</th>
                <th className="px-4 py-3 text-left">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {logs.map((log) => (
                <tr key={log.id || `${log.timestamp}-${log.email}-${log.action}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{formatDate(log.timestamp)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getActionColor(log.action, log.success)}`}>
                      {log.action}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium">{log.email || '-'}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs">
                    {log.details?.error && (
                      <span className="block">{log.details.error}</span>
                    )}
                    {log.details?.contrasena_ingresada && (
                      <span className="block text-xs text-red-600 font-mono">
                        Contrasena: {log.details.contrasena_ingresada}
                      </span>
                    )}
                    {!log.details?.error && (log.details?.name || log.details?.convocatoria_nombre || log.details?.admin_email || log.details?.user_type || '-')}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {log.success ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mx-auto" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500 mx-auto" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400 font-mono">{log.ip_address || '-'}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-slate-500">
                    No se encontraron logs con los filtros seleccionados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const getEstadoBadge = (estado) => {
    const estilos = {
      'pendiente': 'bg-yellow-100 text-yellow-800',
      'en_revision': 'bg-blue-100 text-blue-800',
      'aprobada': 'bg-green-100 text-green-800',
      'rechazada': 'bg-red-100 text-red-800'
    };
    const labels = {
      'pendiente': 'Pendiente',
      'en_revision': 'En Revision',
      'aprobada': 'Aprobada',
      'rechazada': 'Rechazada'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full font-medium ${estilos[estado] || 'bg-slate-100'}`}>
        {labels[estado] || estado}
      </span>
    );
  };

  const renderPostulaciones = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por email del postulante..."
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="search-postulaciones-input"
              />
            </div>
          </div>
          <select 
            value={filterFuncionario} 
            onChange={(e) => { setFilterFuncionario(e.target.value); setPostulacionesPage(1); }} 
            className="px-4 py-2 border rounded-lg"
            data-testid="filter-funcionario"
          >
            <option value="">Todos los funcionarios</option>
            {funcionariosList.map(f => (
              <option key={f.id} value={f.id}>{f.nombre || f.email}</option>
            ))}
          </select>
          <select 
            value={filterConvocatoria} 
            onChange={(e) => { setFilterConvocatoria(e.target.value); setPostulacionesPage(1); }} 
            className="px-4 py-2 border rounded-lg"
            data-testid="filter-convocatoria-post"
          >
            <option value="">Todas las convocatorias</option>
            {convocatoriasList.map(c => (
              <option key={c.id} value={c.id}>{c.codigo} - {c.nombre?.substring(0, 30)}...</option>
            ))}
          </select>
          <select 
            value={filterEstado} 
            onChange={(e) => { setFilterEstado(e.target.value); setPostulacionesPage(1); }} 
            className="px-4 py-2 border rounded-lg"
            data-testid="filter-estado-post"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendiente</option>
            <option value="en_revision">En Revision</option>
            <option value="aprobada">Aprobada</option>
            <option value="rechazada">Rechazada</option>
          </select>
          <button 
            onClick={fetchPostulaciones} 
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            data-testid="search-postulaciones-btn"
          >
            <Search className="h-4 w-4" /> Buscar
          </button>
          <button
            onClick={() => setShowManualPostModal(true)}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            data-testid="manual-postulation-btn"
          >
            <UserPlus className="h-4 w-4" /> Postular Manualmente
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800 flex items-center justify-between">
        <span>Mostrando {postulaciones.length} de {totalPostulaciones} postulaciones</span>
        <span>Pagina {postulacionesPage} de {postulacionesTotalPages}</span>
      </div>

      {/* Postulaciones Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="postulaciones-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">Postulante</th>
                <th className="px-4 py-3 text-left">Cedula</th>
                <th className="px-4 py-3 text-left">Convocatoria</th>
                <th className="px-4 py-3 text-left">Funcionario (Owner)</th>
                <th className="px-4 py-3 text-center">Estado</th>
                <th className="px-4 py-3 text-center">Puntaje</th>
                <th className="px-4 py-3 text-left">Fecha</th>
                <th className="px-4 py-3 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {postulaciones.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{p.user_name || 'N/A'}</div>
                    <div className="text-xs text-slate-500">{p.user_email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm">{p.user_document || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900 max-w-[200px] truncate">
                      {p.convocatoria_info?.nombre || 'N/A'}
                    </div>
                    <div className="text-xs text-slate-500">{p.convocatoria_info?.codigo || '-'}</div>
                    {!p.convocatoria_info?.existe && (
                      <span className="text-xs text-red-500 font-medium">Convocatoria eliminada</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {p.funcionario_info ? (
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-purple-500" />
                        <div>
                          <div className="font-medium text-slate-900">{p.funcionario_info.nombre || 'N/A'}</div>
                          <div className="text-xs text-slate-500">{p.funcionario_info.email}</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-slate-400">Sin asignar</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">{getEstadoBadge(p.estado)}</td>
                  <td className="px-4 py-3 text-center">
                    {p.es_externa ? (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-orange-100 text-orange-800">
                        Externa
                      </span>
                    ) : p.puntaje !== undefined && p.puntaje !== null ? (
                      <span className={`inline-flex items-center px-2 py-1 text-sm font-bold rounded ${
                        p.puntaje >= 80 ? 'bg-green-100 text-green-800' :
                        p.puntaje >= 60 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {p.puntaje}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(p.fecha_postulacion)}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => setViewPostulacionModal({ open: true, postulacion: p })}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      title="Ver detalles"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {postulaciones.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                    No se encontraron postulaciones con los filtros seleccionados
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {postulacionesTotalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between bg-slate-50">
            <div className="text-sm text-slate-600">
              Pagina {postulacionesPage} de {postulacionesTotalPages}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPostulacionesPage(1)}
                disabled={postulacionesPage === 1}
                className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPostulacionesPage(p => Math.max(1, p - 1))}
                disabled={postulacionesPage === 1}
                className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-4 py-1 bg-blue-600 text-white rounded text-sm font-medium">
                {postulacionesPage}
              </span>
              <button
                onClick={() => setPostulacionesPage(p => Math.min(postulacionesTotalPages, p + 1))}
                disabled={postulacionesPage === postulacionesTotalPages}
                className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPostulacionesPage(postulacionesTotalPages)}
                disabled={postulacionesPage === postulacionesTotalPages}
                className="p-2 rounded hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-8" data-testid="superadmin-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Shield className="h-6 w-6 text-red-600" />
                Super Administrador
              </h1>
              <p className="text-slate-500 text-sm">Panel de control total del sistema</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => { fetchDashboard(); if (activeTab === 'users') fetchUsers(); if (activeTab === 'logs') fetchLogs(); if (activeTab === 'postulaciones') fetchPostulaciones(); }} 
              className="px-4 py-2 bg-slate-200 rounded-lg hover:bg-slate-300 flex items-center gap-2 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className="h-4 w-4" /> Actualizar
            </button>
            <button 
              onClick={handleLogout} 
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" /> Cerrar Sesion
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'dashboard' 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-dashboard"
            >
              <BarChart3 className="h-4 w-4" /> Dashboard
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'users' 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-users"
            >
              <Users className="h-4 w-4" /> Usuarios
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'logs' 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-logs"
            >
              <Activity className="h-4 w-4" /> Logs de Actividad
            </button>
            <button
              onClick={() => setActiveTab('postulaciones')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'postulaciones' 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-postulaciones"
            >
              <ClipboardList className="h-4 w-4" /> Postulaciones
            </button>
            <button
              onClick={() => setActiveTab('legacy')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'legacy' 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-legacy"
            >
              <Database className="h-4 w-4" /> Usuarios Antiguos
            </button>
            <button
              onClick={() => setActiveTab('support')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                activeTab === 'support' 
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid="tab-support"
            >
              <LifeBuoy className="h-4 w-4" /> Tickets de Soporte
            </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-10 w-10 animate-spin text-blue-500" />
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'users' && renderUsers()}
            {activeTab === 'logs' && renderLogs()}
            {activeTab === 'postulaciones' && renderPostulaciones()}
            {activeTab === 'legacy' && <LegacyUsersTab token={token} />}
            {activeTab === 'support' && <SupportTicketsTab token={token} />}
          </>
        )}

        <ManualPostulationModal
          open={showManualPostModal}
          onClose={() => setShowManualPostModal(false)}
          token={token}
          convocatorias={convocatoriasList}
          onSuccess={() => fetchPostulaciones()}
        />

        {/* Password Change Modal */}
        {showPasswordModal && selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" data-testid="password-modal">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Key className="h-5 w-5 text-blue-600" />
                Establecer Contrasena Temporal
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Usuario: <strong>{selectedUser.email}</strong>
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  Esta contrasena sera temporal. Se recomienda que el usuario la cambie al iniciar sesion.
                </p>
              </div>
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva contrasena (minimo 6 caracteres)"
                className="w-full px-4 py-2 border rounded-lg mb-4 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="new-password-input"
              />
              <div className="flex justify-end gap-3">
                <button 
                  onClick={() => { setShowPasswordModal(false); setSelectedUser(null); setNewPassword(''); }} 
                  className="px-4 py-2 text-slate-600 border rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid="cancel-password-btn"
                >
                  Cancelar
                </button>
                <button 
                  onClick={handleChangePassword} 
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid="save-password-btn"
                  disabled={!newPassword || newPassword.length < 6}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Postulacion Modal */}
        {viewPostulacionModal.open && viewPostulacionModal.postulacion && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" data-testid="postulacion-modal">
              <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-blue-600" />
                  Detalles de la Postulacion
                </h3>
                <button 
                  onClick={() => setViewPostulacionModal({ open: false, postulacion: null })}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <XCircle className="h-5 w-5 text-slate-400" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Postulante Info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <User className="h-4 w-4" /> Informacion del Postulante
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500">Nombre</p>
                      <p className="font-medium">{viewPostulacionModal.postulacion.user_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="font-medium">{viewPostulacionModal.postulacion.user_email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Cedula/Documento</p>
                      <p className="font-medium font-mono">{viewPostulacionModal.postulacion.user_document || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Tipo Postulante</p>
                      <p className="font-medium capitalize">{viewPostulacionModal.postulacion.tipo_postulante || 'persona'}</p>
                    </div>
                  </div>
                </div>

                {/* Convocatoria Info */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Convocatoria
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <p className="text-xs text-blue-500">Nombre</p>
                      <p className="font-medium">{viewPostulacionModal.postulacion.convocatoria_info?.nombre || viewPostulacionModal.postulacion.convocatoria_nombre}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-500">Codigo</p>
                      <p className="font-medium">{viewPostulacionModal.postulacion.convocatoria_info?.codigo || viewPostulacionModal.postulacion.convocatoria_codigo}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-500">Estado Convocatoria</p>
                      <p className="font-medium">{viewPostulacionModal.postulacion.convocatoria_info?.existe ? 'Activa' : 'Eliminada'}</p>
                    </div>
                  </div>
                </div>

                {/* Funcionario/Owner Info */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-purple-700 mb-3 flex items-center gap-2">
                    <Building className="h-4 w-4" /> Funcionario Responsable (Owner)
                  </h4>
                  {viewPostulacionModal.postulacion.funcionario_info ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-purple-500">Nombre</p>
                        <p className="font-medium">{viewPostulacionModal.postulacion.funcionario_info.nombre || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-purple-500">Email</p>
                        <p className="font-medium">{viewPostulacionModal.postulacion.funcionario_info.email}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500">Sin funcionario asignado</p>
                  )}
                </div>

                {/* Estado y Puntaje */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-amber-50 rounded-lg p-4">
                    <p className="text-xs text-amber-600 mb-1">Estado</p>
                    {getEstadoBadge(viewPostulacionModal.postulacion.estado)}
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-xs text-green-600 mb-1">Puntaje</p>
                    <p className="text-2xl font-bold text-green-700">
                      {viewPostulacionModal.postulacion.puntaje !== undefined && viewPostulacionModal.postulacion.puntaje !== null 
                        ? viewPostulacionModal.postulacion.puntaje 
                        : 'Sin calificar'}
                    </p>
                  </div>
                </div>

                {/* External Link Info */}
                {viewPostulacionModal.postulacion.es_externa && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-orange-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Postulacion Externa
                    </h4>
                    <p className="text-sm text-orange-600 mb-2">
                      Esta postulacion fue registrada para una convocatoria con formulario externo.
                    </p>
                    {viewPostulacionModal.postulacion.enlace_externo && (
                      <a 
                        href={viewPostulacionModal.postulacion.enlace_externo} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 underline break-all"
                      >
                        {viewPostulacionModal.postulacion.enlace_externo}
                      </a>
                    )}
                  </div>
                )}

                {/* Fechas */}
                <div className="border-t pt-4">
                  <p className="text-xs text-slate-500">Fecha de Postulacion</p>
                  <p className="font-medium">{formatDate(viewPostulacionModal.postulacion.fecha_postulacion)}</p>
                </div>

                {/* Observaciones */}
                {viewPostulacionModal.postulacion.observaciones && (
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Observaciones</p>
                    <p className="text-sm">{viewPostulacionModal.postulacion.observaciones}</p>
                  </div>
                )}

                {/* Respuestas del Formulario */}
                {viewPostulacionModal.postulacion.respuestas_formulario && 
                 Object.keys(viewPostulacionModal.postulacion.respuestas_formulario).length > 0 && (
                  <div className="bg-indigo-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-indigo-700 mb-3">Respuestas del Formulario</h4>
                    <div className="space-y-2">
                      {Object.entries(viewPostulacionModal.postulacion.respuestas_formulario).map(([key, value]) => (
                        <div key={key} className="border-b border-indigo-100 pb-2 last:border-0">
                          <p className="text-xs text-indigo-600 font-medium">{key.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-slate-800">{String(value) || '-'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create Funcionario Modal */}
        {showCreateFuncionarioModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="create-funcionario-modal">
              <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-purple-600" />
                  Crear Nuevo Funcionario
                </h3>
                <button 
                  onClick={() => setShowCreateFuncionarioModal(false)}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <XCircle className="h-5 w-5 text-slate-400" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700">
                  Los funcionarios creados aqui seran verificados automaticamente y podran gestionar convocatorias.
                </div>
                
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Correo Electronico <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={newFuncionario.email}
                    onChange={(e) => setNewFuncionario({...newFuncionario, email: e.target.value})}
                    placeholder="correo@ejemplo.com"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    data-testid="input-funcionario-email"
                  />
                </div>
                
                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Contrasena <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newFuncionario.password}
                    onChange={(e) => setNewFuncionario({...newFuncionario, password: e.target.value})}
                    placeholder="Minimo 6 caracteres"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    data-testid="input-funcionario-password"
                  />
                </div>
                
                {/* Names */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Primer Nombre <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newFuncionario.first_name}
                      onChange={(e) => setNewFuncionario({...newFuncionario, first_name: e.target.value})}
                      placeholder="Primer nombre"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Segundo Nombre
                    </label>
                    <input
                      type="text"
                      value={newFuncionario.second_name}
                      onChange={(e) => setNewFuncionario({...newFuncionario, second_name: e.target.value})}
                      placeholder="Segundo nombre"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
                
                {/* Lastnames */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Primer Apellido <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newFuncionario.first_lastname}
                      onChange={(e) => setNewFuncionario({...newFuncionario, first_lastname: e.target.value})}
                      placeholder="Primer apellido"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Segundo Apellido
                    </label>
                    <input
                      type="text"
                      value={newFuncionario.second_lastname}
                      onChange={(e) => setNewFuncionario({...newFuncionario, second_lastname: e.target.value})}
                      placeholder="Segundo apellido"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
                
                {/* Document */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Tipo Doc.
                    </label>
                    <select
                      value={newFuncionario.document_type}
                      onChange={(e) => setNewFuncionario({...newFuncionario, document_type: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="CC">CC</option>
                      <option value="CE">CE</option>
                      <option value="Pasaporte">Pasaporte</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Numero de Documento <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newFuncionario.document_id}
                      onChange={(e) => setNewFuncionario({...newFuncionario, document_id: e.target.value})}
                      placeholder="Numero de documento"
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button 
                    onClick={() => setShowCreateFuncionarioModal(false)} 
                    className="px-4 py-2 text-slate-600 border rounded-lg hover:bg-slate-50 transition-colors"
                    disabled={creatingFuncionario}
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleCreateFuncionario} 
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                    disabled={creatingFuncionario}
                    data-testid="btn-save-funcionario"
                  >
                    {creatingFuncionario ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" /> Creando...
                      </>
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4" /> Crear Funcionario
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUserModal && editingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" data-testid="edit-user-modal">
              <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Edit className="h-5 w-5 text-amber-600" />
                  Editar Usuario: {editingUser.email}
                </h3>
                <button 
                  onClick={handleCloseEditModal}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <XCircle className="h-5 w-5 text-slate-400" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Info Banner */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-800 font-medium">Edición con seguridad PIN</p>
                    <p className="text-xs text-amber-600 mt-1">
                      Tipo de usuario: <span className="font-semibold uppercase">{editingUser.user_type}</span> | 
                      Los cambios requieren confirmación con PIN de seguridad.
                    </p>
                  </div>
                </div>

                {/* Datos de Acceso */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <Key className="h-4 w-4" /> Datos de Acceso
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Correo Electrónico
                      </label>
                      <input
                        type="email"
                        value={editUserData.email || ''}
                        onChange={(e) => handleEditFieldChange('email', e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                        data-testid="edit-email"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Estado de Verificación
                      </label>
                      <select
                        value={editUserData.verified ? 'true' : 'false'}
                        onChange={(e) => handleEditFieldChange('verified', e.target.value === 'true')}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-amber-500"
                      >
                        <option value="true">Verificado</option>
                        <option value="false">Sin verificar</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Datos para Persona Natural o Funcionario */}
                {(editingUser.user_type === 'natural' || editingUser.user_type === 'funcionario') && (
                  <>
                    {/* Datos Personales */}
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-700 mb-4 flex items-center gap-2">
                        <User className="h-4 w-4" /> Datos Personales
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Primer Nombre</label>
                          <input
                            type="text"
                            value={editUserData.first_name || ''}
                            onChange={(e) => handleEditFieldChange('first_name', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Segundo Nombre</label>
                          <input
                            type="text"
                            value={editUserData.second_name || ''}
                            onChange={(e) => handleEditFieldChange('second_name', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Primer Apellido</label>
                          <input
                            type="text"
                            value={editUserData.first_lastname || ''}
                            onChange={(e) => handleEditFieldChange('first_lastname', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Segundo Apellido</label>
                          <input
                            type="text"
                            value={editUserData.second_lastname || ''}
                            onChange={(e) => handleEditFieldChange('second_lastname', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Documento */}
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-green-700 mb-4 flex items-center gap-2">
                        <FileText className="h-4 w-4" /> Documento de Identidad
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Tipo de Documento</label>
                          <select
                            value={editUserData.document_type || 'CC'}
                            onChange={(e) => handleEditFieldChange('document_type', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          >
                            <option value="CC">Cédula de Ciudadanía</option>
                            <option value="CE">Cédula de Extranjería</option>
                            <option value="Pasaporte">Pasaporte</option>
                            <option value="TI">Tarjeta de Identidad</option>
                            <option value="PEP">PEP</option>
                            <option value="PPT">PPT</option>
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-slate-600 mb-1">Número de Documento</label>
                          <input
                            type="text"
                            value={editUserData.document_id || ''}
                            onChange={(e) => handleEditFieldChange('document_id', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Datos Adicionales */}
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-purple-700 mb-4 flex items-center gap-2">
                        <Activity className="h-4 w-4" /> Información Adicional
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Teléfono</label>
                          <input
                            type="text"
                            value={editUserData.phone || ''}
                            onChange={(e) => handleEditFieldChange('phone', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Sexo Biológico</label>
                          <select
                            value={editUserData.biological_sex || ''}
                            onChange={(e) => handleEditFieldChange('biological_sex', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="">Seleccione...</option>
                            <option value="Masculino">Masculino</option>
                            <option value="Femenino">Femenino</option>
                            <option value="Intersexual">Intersexual</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Fecha de Nacimiento</label>
                          <input
                            type="date"
                            value={editUserData.birth_date || ''}
                            onChange={(e) => handleEditFieldChange('birth_date', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">País de Residencia</label>
                          <input
                            type="text"
                            value={editUserData.residence_country || ''}
                            onChange={(e) => handleEditFieldChange('residence_country', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Departamento</label>
                          <input
                            type="text"
                            value={editUserData.residence_department || ''}
                            onChange={(e) => handleEditFieldChange('residence_department', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Municipio</label>
                          <input
                            type="text"
                            value={editUserData.residence_municipality || ''}
                            onChange={(e) => handleEditFieldChange('residence_municipality', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Datos para Persona Jurídica */}
                {editingUser.user_type === 'juridica' && (
                  <>
                    {/* Datos de la Empresa */}
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-blue-700 mb-4 flex items-center gap-2">
                        <Building className="h-4 w-4" /> Datos de la Empresa
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Razón Social</label>
                          <input
                            type="text"
                            value={editUserData.company_name || ''}
                            onChange={(e) => handleEditFieldChange('company_name', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">NIT</label>
                          <input
                            type="text"
                            value={editUserData.nit || ''}
                            onChange={(e) => handleEditFieldChange('nit', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Representante Legal */}
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-green-700 mb-4 flex items-center gap-2">
                        <User className="h-4 w-4" /> Representante Legal
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Primer Nombre</label>
                          <input
                            type="text"
                            value={editUserData.legal_rep_first_name || ''}
                            onChange={(e) => handleEditFieldChange('legal_rep_first_name', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Segundo Nombre</label>
                          <input
                            type="text"
                            value={editUserData.legal_rep_second_name || ''}
                            onChange={(e) => handleEditFieldChange('legal_rep_second_name', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Primer Apellido</label>
                          <input
                            type="text"
                            value={editUserData.legal_rep_first_lastname || ''}
                            onChange={(e) => handleEditFieldChange('legal_rep_first_lastname', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Segundo Apellido</label>
                          <input
                            type="text"
                            value={editUserData.legal_rep_second_lastname || ''}
                            onChange={(e) => handleEditFieldChange('legal_rep_second_lastname', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Persona Asociada */}
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-purple-700 mb-4 flex items-center gap-2">
                        <Users className="h-4 w-4" /> Persona Asociada al Usuario
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Tipo Doc.</label>
                          <select
                            value={editUserData.assoc_document_type || 'CC'}
                            onChange={(e) => handleEditFieldChange('assoc_document_type', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="CC">C.C.</option>
                            <option value="CE">C.E.</option>
                            <option value="Pasaporte">Pasaporte</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">No. Documento</label>
                          <input
                            type="text"
                            value={editUserData.assoc_document_id || ''}
                            onChange={(e) => handleEditFieldChange('assoc_document_id', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Primer Nombre</label>
                          <input
                            type="text"
                            value={editUserData.assoc_first_name || ''}
                            onChange={(e) => handleEditFieldChange('assoc_first_name', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Segundo Nombre</label>
                          <input
                            type="text"
                            value={editUserData.assoc_second_name || ''}
                            onChange={(e) => handleEditFieldChange('assoc_second_name', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Primer Apellido</label>
                          <input
                            type="text"
                            value={editUserData.assoc_first_lastname || ''}
                            onChange={(e) => handleEditFieldChange('assoc_first_lastname', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Segundo Apellido</label>
                          <input
                            type="text"
                            value={editUserData.assoc_second_lastname || ''}
                            onChange={(e) => handleEditFieldChange('assoc_second_lastname', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Email</label>
                          <input
                            type="email"
                            value={editUserData.assoc_email || ''}
                            onChange={(e) => handleEditFieldChange('assoc_email', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Teléfono</label>
                          <input
                            type="text"
                            value={editUserData.assoc_phone || ''}
                            onChange={(e) => handleEditFieldChange('assoc_phone', e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button 
                    onClick={handleCloseEditModal} 
                    className="px-4 py-2 text-slate-600 border rounded-lg hover:bg-slate-50 transition-colors"
                    disabled={savingUser}
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleRequestSaveUser} 
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2"
                    disabled={savingUser}
                    data-testid="btn-save-user"
                  >
                    <Save className="h-4 w-4" /> Guardar Cambios
                  </button>
                </div>
              </div>

              {/* PIN Confirmation Modal */}
              {showPinConfirm && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
                  <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6" data-testid="pin-confirm-modal">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-amber-100 rounded-full">
                        <Shield className="h-6 w-6 text-amber-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">Confirmación de Seguridad</h4>
                        <p className="text-xs text-slate-500">Ingrese el PIN para confirmar los cambios</p>
                      </div>
                    </div>
                    
                    <div className="bg-slate-50 rounded-lg p-4 mb-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        PIN de Seguridad (6 dígitos)
                      </label>
                      <input
                        type="password"
                        maxLength={6}
                        value={pinInput}
                        onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                        placeholder="••••••"
                        className="w-full px-4 py-3 text-center text-2xl tracking-widest border-2 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        data-testid="pin-input"
                        autoFocus
                      />
                    </div>

                    <div className="flex gap-3">
                      <button 
                        onClick={() => { setShowPinConfirm(false); setPinInput(''); }} 
                        className="flex-1 px-4 py-2 text-slate-600 border rounded-lg hover:bg-slate-50 transition-colors"
                        disabled={savingUser}
                      >
                        Cancelar
                      </button>
                      <button 
                        onClick={handleSaveUserWithPin} 
                        className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors flex items-center justify-center gap-2"
                        disabled={savingUser || pinInput.length !== 6}
                        data-testid="confirm-pin-btn"
                      >
                        {savingUser ? (
                          <>
                            <RefreshCw className="h-4 w-4 animate-spin" /> Guardando...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4" /> Confirmar
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteUserModal && deletingUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full" data-testid="delete-user-modal">
              <div className="p-6 border-b flex justify-between items-center">
                <h3 className="text-lg font-semibold flex items-center gap-2 text-red-600">
                  <Trash2 className="h-5 w-5" />
                  Eliminar Usuario
                </h3>
                <button 
                  onClick={handleCloseDeleteModal}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <XCircle className="h-5 w-5 text-slate-400" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Warning */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">¿Está seguro de eliminar este usuario?</p>
                      <p className="text-xs text-red-600 mt-1">Esta acción no se puede deshacer.</p>
                    </div>
                  </div>
                </div>

                {/* User Info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-medium">{deletingUser.email}</span>
                  </div>
                  <div className="text-sm text-slate-600">
                    <span className="font-medium">Nombre:</span> {deletingUser.first_name || deletingUser.company_name || 'N/A'} {deletingUser.first_lastname || ''}
                  </div>
                  <div className="text-sm text-slate-600">
                    <span className="font-medium">Tipo:</span> {getUserTypeBadge(deletingUser.user_type)}
                  </div>
                </div>

                {/* PIN Input */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    PIN de Seguridad para Eliminación (6 dígitos)
                  </label>
                  <input
                    type="password"
                    maxLength={6}
                    value={deletePin}
                    onChange={(e) => setDeletePin(e.target.value.replace(/\D/g, ''))}
                    placeholder="••••••"
                    className="w-full px-4 py-3 text-center text-2xl tracking-widest border-2 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    data-testid="delete-pin-input"
                    autoFocus
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button 
                    onClick={handleCloseDeleteModal} 
                    className="flex-1 px-4 py-2 text-slate-600 border rounded-lg hover:bg-slate-50 transition-colors"
                    disabled={isDeleting}
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={handleDeleteUserWithPin} 
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    disabled={isDeleting || deletePin.length !== 6}
                    data-testid="confirm-delete-btn"
                  >
                    {isDeleting ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" /> Eliminando...
                      </>
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4" /> Eliminar
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
